import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, CheckCircle2, Clock, Flame, Users, TrendingUp, ShoppingBag, AlertTriangle, Heart, Dumbbell } from 'lucide-react';
import { toast } from 'sonner';
import HuscleosHeader from '../../components/huscleos/HuscleosHeader';

const API = process.env.REACT_APP_BACKEND_URL || '';
const C = { accent: '#FF2E00', dark: '#0F0F0F', text: '#1A1A1A', muted: '#6B7280', light: '#9CA3AF', border: '#E5E7EB' };

function ProgressRing({ pct }) {
  const r = 38, circ = 2 * Math.PI * r, offset = circ - (pct / 100) * circ;
  return (
    <svg width="96" height="96" className="transform -rotate-90">
      <circle cx="48" cy="48" r={r} fill="none" stroke="#E5E7EB" strokeWidth="6" />
      <circle cx="48" cy="48" r={r} fill="none" stroke={C.accent} strokeWidth="6" strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round" className="transition-all duration-700" />
    </svg>
  );
}

export default function MerchPage() {
  const [drops, setDrops] = useState([]);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', phone: '', size: 'L', quantity: 1 });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetch(`${API}/api/merch/drops`).then(r => r.json()).then(d => setDrops(d.drops || []));
  }, []);

  const handleOrder = async (dropId) => {
    if (!form.name || !form.email) { toast.error('Name and email required.'); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/merch/drops/${dropId}/orders`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, quantity: parseInt(form.quantity) || 1 }),
      });
      if (!res.ok) throw new Error();
      setDone(true);
      toast.success('Pre-order locked in!');
    } catch { toast.error('Something went wrong.'); }
    finally { setSubmitting(false); }
  };

  const getCurrentPrice = (drop) => {
    const units = (drop.total_units || 0) + (parseInt(form.quantity) || 1);
    const tiers = (drop.price_tiers || []).sort((a, b) => b.min_qty - a.min_qty);
    for (const t of tiers) { if (units >= t.min_qty) return t.price; }
    return tiers.length ? tiers[tiers.length - 1].price : 30;
  };

  if (drops.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: C.dark, fontFamily: 'Manrope, sans-serif' }}>
        <div className="text-center px-4">
          <ShoppingBag size={48} className="mx-auto mb-4" style={{ color: '#333' }} />
          <h1 className="text-2xl tracking-tighter uppercase mb-2" style={{ fontFamily: 'Bebas Neue', color: '#fff' }}>NO ACTIVE DROPS</h1>
          <p className="text-sm text-gray-500 mb-6">Check back soon — new merch drops are coming.</p>
          <Link to="/" className="text-sm font-bold text-[#FF2E00] hover:text-white transition-colors"><ArrowLeft size={14} className="inline mr-1" /> Back to HuscleOS</Link>
        </div>
      </div>
    );
  }

  return (
    <div style={{ fontFamily: 'Manrope, sans-serif', background: '#FAFAFA' }}>
      <HuscleosHeader variant="solid" />
      <div className="h-16" />

      {/* Hero */}
      <section className="py-12 sm:py-16 text-center" style={{ background: C.dark }}>
        <div className="max-w-3xl mx-auto px-4">
          <p className="text-sm font-bold tracking-[0.2em] uppercase mb-3" style={{ color: C.accent }}>MERCH DROPS · POWERED BY FIT PRINT BROS</p>
          <h1 className="text-4xl sm:text-5xl tracking-tighter uppercase leading-none mb-3" style={{ fontFamily: 'Bebas Neue', color: '#fff' }}>GEAR UP.</h1>
          <p className="text-sm text-gray-400 max-w-xl mx-auto">Pre-order limited drops. More orders = lower price for everyone. Don't sleep on it.</p>
        </div>
      </section>

      {/* How merch grows your gym */}
      <section className="py-14 sm:py-20 bg-white border-b" style={{ borderColor: C.border }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <p className="text-sm font-bold tracking-[0.2em] uppercase mb-3" style={{ color: C.accent }}>GROWTH · RETENTION · BRAND</p>
            <h2 className="text-3xl sm:text-4xl tracking-tighter uppercase leading-none mb-3" style={{ fontFamily: 'Bebas Neue', color: C.dark }}>
              MERCH IS A GROWTH ENGINE.<br />WE MAKE IT <span style={{ color: C.accent }}>SIMPLE</span>.
            </h2>
            <p className="text-sm text-gray-500 max-w-xl mx-auto">Every shirt a member wears is a billboard for your gym. HuscleOS turns that into a system.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-6 border" style={{ borderColor: C.border }} data-testid="merch-why-1">
              <div className="w-10 h-10 flex items-center justify-center bg-gray-100 mb-4">
                <Users size={18} style={{ color: C.dark }} />
              </div>
              <p className="text-[10px] font-bold tracking-widest uppercase text-gray-400 mb-1">Grow</p>
              <h3 className="text-lg font-bold mb-2" style={{ fontFamily: 'Manrope', color: C.dark }}>Turn members into walking ads.</h3>
              <p className="text-sm text-gray-600 leading-relaxed">A member in your hoodie at Whole Foods is a lead you never had to pay for. Drops keep your brand in the wild.</p>
            </div>

            <div className="p-6 border bg-gray-900" style={{ borderColor: C.dark }} data-testid="merch-why-2">
              <div className="w-10 h-10 flex items-center justify-center bg-[#FF2E00]/15 mb-4">
                <Heart size={18} style={{ color: C.accent }} />
              </div>
              <p className="text-[10px] font-bold tracking-widest uppercase mb-1" style={{ color: C.accent }}>Retain</p>
              <h3 className="text-lg font-bold mb-2 text-white" style={{ fontFamily: 'Manrope' }}>Identity keeps people around.</h3>
              <p className="text-sm text-gray-300 leading-relaxed">Members who rep your gear stay 40% longer on average. Belonging beats discounts every time.</p>
            </div>

            <div className="p-6 border" style={{ borderColor: C.border }} data-testid="merch-why-3">
              <div className="w-10 h-10 flex items-center justify-center bg-gray-100 mb-4">
                <TrendingUp size={18} style={{ color: C.dark }} />
              </div>
              <p className="text-[10px] font-bold tracking-widest uppercase text-gray-400 mb-1">Revenue</p>
              <h3 className="text-lg font-bold mb-2" style={{ fontFamily: 'Manrope', color: C.dark }}>A second revenue stream.</h3>
              <p className="text-sm text-gray-600 leading-relaxed">No upfront inventory. Group-order pricing scales as volume grows. Margin goes straight to your bottom line.</p>
            </div>
          </div>

          <div className="mt-10 p-6 bg-gray-50 border flex flex-col sm:flex-row items-start sm:items-center gap-4" style={{ borderColor: C.border }}>
            <div className="flex items-center justify-center w-10 h-10 bg-white border shrink-0" style={{ borderColor: C.border }}>
              <Dumbbell size={16} style={{ color: C.accent }} />
            </div>
            <div className="flex-1">
              <p className="text-sm font-bold mb-1" style={{ color: C.dark }}>The HuscleOS play: stupid simple.</p>
              <p className="text-sm text-gray-600 leading-relaxed">You tell us what you want. We design, print, ship. You keep the margin. Members keep the gear. Your gym keeps growing.</p>
            </div>
            <Link
              to="/#quiz"
              data-testid="merch-why-cta"
              className="shrink-0 text-xs font-bold tracking-widest uppercase px-5 py-2.5 hover:opacity-90 transition-colors whitespace-nowrap"
              style={{ fontFamily: 'Manrope', background: C.accent, color: '#fff' }}
            >
              Talk to us
            </Link>
          </div>
        </div>
      </section>

      {/* Drops */}
      <section className="py-10 sm:py-14">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {drops.map(drop => {
              const pct = drop.goal_quantity > 0 ? Math.min(100, Math.round((drop.total_units / drop.goal_quantity) * 100)) : 0;
              const isSelected = selected === drop.id;
              const price = getCurrentPrice(drop);
              const deadline = drop.deadline ? new Date(drop.deadline) : null;
              const daysLeft = deadline ? Math.max(0, Math.ceil((deadline.getTime() - Date.now()) / 86400000)) : null;

              return (
                <div key={drop.id} className="bg-white border overflow-hidden" style={{ borderColor: C.border }} data-testid={`merch-drop-${drop.id}`}>
                  {/* Drop header */}
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <p className="text-xs font-bold tracking-widest uppercase mb-1" style={{ color: C.accent }}>{drop.product_type}</p>
                        <h2 className="text-2xl tracking-tighter uppercase" style={{ fontFamily: 'Bebas Neue', color: C.dark }}>{drop.title}</h2>
                        {drop.description && <p className="text-sm text-gray-500 mt-1">{drop.description}</p>}
                      </div>
                      {drop.image_url && <img src={drop.image_url} alt="" className="w-20 h-20 object-cover rounded" />}
                    </div>

                    {/* Stats strip */}
                    <div className="flex items-center gap-6 py-3 border-y" style={{ borderColor: C.border }}>
                      <div className="text-center">
                        <p className="text-lg font-bold" style={{ color: C.dark }}>{drop.total_units || 0}</p>
                        <p className="text-[10px] uppercase tracking-wider text-gray-400">Units</p>
                      </div>
                      <div className="text-center">
                        <p className="text-lg font-bold" style={{ color: C.dark }}>{drop.goal_quantity}</p>
                        <p className="text-[10px] uppercase tracking-wider text-gray-400">Goal</p>
                      </div>
                      <div className="flex-1">
                        <div className="w-full h-3 rounded-full bg-gray-100 overflow-hidden">
                          <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, background: pct >= 100 ? '#10B981' : C.accent }} />
                        </div>
                        <p className="text-[10px] text-gray-400 mt-1">{pct}% funded</p>
                      </div>
                      {daysLeft !== null && (
                        <div className="text-center">
                          <p className="text-lg font-bold" style={{ color: daysLeft <= 3 ? C.accent : C.dark }}>{daysLeft}</p>
                          <p className="text-[10px] uppercase tracking-wider text-gray-400">Days Left</p>
                        </div>
                      )}
                    </div>

                    {/* Price tiers */}
                    <div className="mt-4">
                      <p className="text-[10px] font-bold tracking-widest uppercase text-gray-400 mb-2">PRICE TIERS — More orders = less you pay</p>
                      <div className="flex flex-wrap gap-2">
                        {(drop.price_tiers || []).map((t, i) => {
                          const active = (drop.total_units || 0) >= t.min_qty;
                          return (
                            <div key={i} className={`px-3 py-2 border text-center ${active ? 'border-[#FF2E00] bg-red-50' : ''}`} style={{ borderColor: active ? C.accent : C.border }}>
                              <p className="text-xs font-bold" style={{ color: active ? C.accent : C.dark }}>${t.price}</p>
                              <p className="text-[10px] text-gray-400">{t.min_qty}+ units</p>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Current price callout */}
                    <div className="mt-4 p-3 rounded-lg border-2 text-center" style={{ borderColor: C.accent, background: '#FFF8F6' }}>
                      <p className="text-xs text-gray-500">Current price per unit</p>
                      <p className="text-3xl font-bold" style={{ color: C.accent }}>${price}</p>
                      <p className="text-[10px] text-gray-400 mt-1">Price drops as more people order</p>
                    </div>

                    {/* FOMO warning */}
                    <div className="mt-3 flex items-center gap-2 p-2.5 bg-amber-50 rounded border border-amber-200">
                      <AlertTriangle size={14} className="text-amber-500 shrink-0" />
                      <p className="text-xs text-amber-700 font-medium">Reserve spots are not guaranteed without prepay. Lock yours in.</p>
                    </div>

                    {/* Toggle order form */}
                    {!isSelected ? (
                      <button onClick={() => { setSelected(drop.id); setDone(false); }} data-testid={`preorder-btn-${drop.id}`}
                        className="w-full mt-4 flex items-center justify-center gap-2 text-sm font-bold tracking-widest uppercase py-4 transition-all hover:opacity-90"
                        style={{ background: C.accent, color: '#fff' }}>
                        <Flame size={16} /> PRE-ORDER NOW
                      </button>
                    ) : done ? (
                      <div className="mt-4 p-4 text-center bg-green-50 border border-green-200 rounded">
                        <CheckCircle2 size={24} className="text-green-600 mx-auto mb-2" />
                        <p className="text-sm font-bold text-green-800">You're in! Pre-order locked.</p>
                        <p className="text-xs text-green-600 mt-1">We'll notify you when this drop ships. Prepaid orders get priority.</p>
                      </div>
                    ) : (
                      <div className="mt-4 space-y-3 p-4 border rounded" style={{ borderColor: C.border, background: '#FAFAFA' }}>
                        <div className="grid grid-cols-2 gap-3">
                          <input placeholder="Name *" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} data-testid="merch-name"
                            className="p-3 text-sm border rounded focus:outline-none focus:border-[#FF2E00]" style={{ borderColor: C.border }} />
                          <input placeholder="Email *" type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} data-testid="merch-email"
                            className="p-3 text-sm border rounded focus:outline-none focus:border-[#FF2E00]" style={{ borderColor: C.border }} />
                        </div>
                        <input placeholder="Phone (optional)" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} data-testid="merch-phone"
                          className="w-full p-3 text-sm border rounded focus:outline-none focus:border-[#FF2E00]" style={{ borderColor: C.border }} />
                        <div className="grid grid-cols-2 gap-3">
                          <select value={form.size} onChange={e => setForm(f => ({ ...f, size: e.target.value }))} data-testid="merch-size"
                            className="p-3 text-sm border rounded" style={{ borderColor: C.border }}>
                            {(drop.sizes_available || ['S','M','L','XL','2XL']).map(s => <option key={s} value={s}>{s}</option>)}
                          </select>
                          <select value={form.quantity} onChange={e => setForm(f => ({ ...f, quantity: e.target.value }))} data-testid="merch-qty"
                            className="p-3 text-sm border rounded" style={{ borderColor: C.border }}>
                            {[1,2,3,4,5,6,7,8,9,10].map(n => <option key={n} value={n}>{n} unit{n > 1 ? 's' : ''}</option>)}
                          </select>
                        </div>
                        <div className="flex items-center justify-between p-3 rounded" style={{ background: '#fff', border: `2px solid ${C.accent}` }}>
                          <span className="text-sm text-gray-500">Your total</span>
                          <span className="text-xl font-bold" style={{ color: C.accent }}>${price * (parseInt(form.quantity) || 1)}</span>
                        </div>
                        <button onClick={() => handleOrder(drop.id)} disabled={submitting} data-testid="merch-submit"
                          className="w-full flex items-center justify-center gap-2 text-sm font-bold tracking-widest uppercase py-4 transition-all hover:opacity-90 disabled:opacity-50"
                          style={{ background: C.accent, color: '#fff' }}>
                          {submitting ? 'LOCKING IN...' : 'LOCK IN MY ORDER'} {!submitting && <ArrowRight size={16} />}
                        </button>
                        <p className="text-[10px] text-center text-gray-400">Payment collected when the drop closes. Prepaid orders ship first.</p>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t text-center" style={{ borderColor: C.border }}>
        <p className="text-xs text-gray-400">Printed & shipped by <a href="https://fitprintbros.com" target="_blank" rel="noopener noreferrer" className="font-bold text-gray-600 hover:text-gray-900">Fit Print Bros</a> — Professional screen printing, DTF, and embroidery.</p>
        <Link to="/" className="text-xs text-gray-400 hover:text-gray-600 mt-2 inline-block">Powered by HuscleOS</Link>
      </footer>
    </div>
  );
}
